import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import VippsButton from "@/components/VippsButton";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Lock, ArrowLeft, Check, RefreshCw, X } from "lucide-react";

const PRICE_NOK = 79;

export default function Checkout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("vipps");
  const [autoRenew, setAutoRenew] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleVipps = async () => {
    setLoading(true);
    try {
      // Use Recurring API when auto-renew is on, ePayment for one-time
      const endpoint = autoRenew
        ? "/payment/vipps/subscribe"
        : "/payment/vipps/initiate";
      const res = await api.post(endpoint);
      window.location.href = res.data.redirect_url;
    } catch (err) {
      const msg = err.response?.data?.detail || "Kunne ikke koble til Vipps. Prøv igjen.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleStripe = async () => {
    setLoading(true);
    try {
      const res = await api.post("/payment/stripe/create-session");
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const msg = err.response?.data?.detail || "Kunne ikke åpne kortbetaling. Prøv igjen.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-lg mx-auto px-4 py-12">
        <button
          onClick={() => navigate("/pricing")}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Tilbake til priser
        </button>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          {/* Order summary */}
          <div className="pb-5 border-b border-gray-100 mb-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-semibold text-gray-900">VeiKlar Full tilgang</div>
                <div className="text-sm text-gray-500 mt-0.5">30 dager · fra {new Date().toLocaleDateString("nb-NO", { day: "numeric", month: "long" })}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{PRICE_NOK} kr</div>
                <div className="text-xs text-gray-400">/ måned</div>
              </div>
            </div>

            {/* Auto-renew toggle */}
            <button
              onClick={() => setAutoRenew(!autoRenew)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all ${
                autoRenew
                  ? "border-fjord bg-fjord/5"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <RefreshCw className={`w-4 h-4 ${autoRenew ? "text-fjord" : "text-gray-400"}`} />
                <div className="text-left">
                  <div className={`text-sm font-semibold ${autoRenew ? "text-fjord" : "text-gray-700"}`}>
                    Automatisk fornyelse
                  </div>
                  <div className="text-xs text-gray-500">
                    {autoRenew ? "79 kr trekkes automatisk hver måned · kanseller når som helst" : "Manuell betaling – du betaler igjen neste måned selv"}
                  </div>
                </div>
              </div>
              <div className={`w-10 h-6 rounded-full transition-colors flex items-center ${autoRenew ? "bg-fjord justify-end" : "bg-gray-300 justify-start"}`}>
                <div className="w-4 h-4 bg-white rounded-full mx-1 shadow-sm" />
              </div>
            </button>
          </div>

          {/* Method tabs */}
          <div className="flex gap-2 mb-5">
            <button
              onClick={() => setMethod("vipps")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold transition-colors
                ${method === "vipps"
                  ? "border-[#FF5B24] bg-orange-50 text-[#FF5B24]"
                  : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
            >
              <div className="w-5 h-5 bg-[#FF5B24] rounded-full grid place-items-center">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                  <path d="M8 8l4 8 4-8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              Vipps
              <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-bold">Anbefalt</span>
            </button>
            <button
              onClick={() => setMethod("card")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold transition-colors
                ${method === "card"
                  ? "border-fjord bg-fjord/5 text-fjord"
                  : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
            >
              <CreditCard className="w-4 h-4" />
              Kort
            </button>
          </div>

          {/* Vipps panel */}
          {method === "vipps" && (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 text-sm text-gray-700 space-y-1.5">
                {[
                  "Bekreft betalingen i Vipps-appen din",
                  "Ikke noe kortnummer nødvendig",
                  autoRenew ? "Kanseller abonnementet når som helst" : "Tilgang i 30 dager · forny manuelt",
                ].map((s) => (
                  <div key={s} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF5B24] shrink-0 mt-0.5" />
                    {s}
                  </div>
                ))}
              </div>
              <VippsButton
                onClick={handleVipps}
                loading={loading}
                className="w-full justify-center"
              />
            </div>
          )}

          {/* Card panel */}
          {method === "card" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-gray-700 space-y-1.5">
                {[
                  "Visa, Mastercard og andre kort",
                  "Behandles sikkert av Stripe",
                  "Tilgang gis umiddelbart etter betaling",
                ].map((s) => (
                  <div key={s} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-fjord shrink-0 mt-0.5" />
                    {s}
                  </div>
                ))}
              </div>
              <Button
                onClick={handleStripe}
                disabled={loading}
                className="w-full bg-fjord hover:bg-fjord/90 gap-2"
              >
                {loading
                  ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <CreditCard className="w-4 h-4" />
                }
                {loading ? "Åpner betaling…" : `Betal ${PRICE_NOK} kr med kort`}
              </Button>
            </div>
          )}

          {/* Security note */}
          <div className="flex flex-col items-center gap-1 mt-5 text-xs text-gray-400">
            <div className="flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" />
              Sikker betaling · kortinfo lagres aldri av VeiKlar
            </div>
            <div>14 dagers angrerett · {autoRenew ? "kanseller abonnement når som helst" : "ingen automatisk fornyelse"}</div>
          </div>
        </div>

        {user && (
          <p className="text-center text-sm text-gray-400 mt-4">
            Tilgang legges til <span className="text-gray-600 font-medium">{user.email || user.name}</span>
          </p>
        )}
      </main>
    </div>
  );
}
